import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { TERMS, TermsBody } from "@/data/terms";
import { ShieldCheck, ScrollText, ArrowDown, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TermsAcceptanceGate() {
  const { user, refreshUser } = useAuth();
  const { lang } = useLang();
  const [serverVersion, setServerVersion] = useState(null);
  const [scrolledEnd, setScrolledEnd] = useState(false);
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/terms/version`)
      .then(res => { if (!cancelled) setServerVersion(res.data?.version || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Visibility logic
  if (!user || !user.email || !serverVersion) return null;
  // Skip for the preview placeholder user (no real account)
  if (user.user_id === "preview-user" || user.user_id === "preview_admin") return null;
  const accepted = user.terms_accepted_version === serverVersion;
  if (accepted) return null;

  const terms = TERMS[lang] || TERMS.de;

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 24;
    if (atBottom && !scrolledEnd) setScrolledEnd(true);
  };

  const accept = async () => {
    if (!checked || submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/accept-terms`, {}, { withCredentials: true });
      await refreshUser();
    } catch (e) {
      // even on failure, hide the gate? No — keep it open so user retries.
      console.error("accept-terms failed", e);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="terms-acceptance-gate"
      className="fixed inset-0 z-[100000] flex items-center justify-center p-4 bg-black/85 backdrop-blur-md"
    >
      <div className="glass-surface w-full max-w-3xl max-h-[90vh] flex flex-col rounded-2xl border border-[#00F0FF]/20 shadow-[0_0_60px_rgba(0,240,255,0.15)] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-5 border-b border-white/5 bg-gradient-to-b from-[#00F0FF]/[0.06] to-transparent flex items-start gap-4">
          <div className="w-12 h-12 flex items-center justify-center neon-border rounded-xl flex-shrink-0">
            <ShieldCheck className="w-5 h-5 text-[#00F0FF]" />
          </div>
          <div className="flex-1 min-w-0">
            <span className="text-[10px] font-['JetBrains_Mono'] text-[#00F0FF] tracking-[0.3em] uppercase block mb-1">
              {terms.footer.welcomeTitle}
            </span>
            <h2 className="font-['Outfit'] text-xl font-bold text-white leading-tight mb-1">
              {terms.title}
            </h2>
            <p className="text-xs text-[#A0A0B0] leading-relaxed">
              {terms.footer.requiredAcceptance}
            </p>
          </div>
        </div>

        {/* Scrollable terms body */}
        <div
          ref={scrollRef}
          onScroll={onScroll}
          data-testid="terms-scroll-area"
          className="flex-1 overflow-y-auto px-6 py-5 bg-[#06060C]"
        >
          <p className="text-sm text-[#A0A0B0] mb-5">{terms.intro}</p>
          <TermsBody terms={terms} />
          <div className="mt-6 pt-4 border-t border-white/5 text-[11px] font-['JetBrains_Mono'] text-[#606070]">
            {terms.footer.lastUpdated}: 27.04.2026 · v{serverVersion}
          </div>
        </div>

        {/* Scroll hint */}
        {!scrolledEnd && (
          <div className="px-6 pt-2 -mt-2 flex items-center justify-center gap-1.5 text-[11px] text-[#FFB800] animate-pulse" data-testid="terms-scroll-hint">
            <ArrowDown className="w-3 h-3" /> {terms.footer.mustScroll}
          </div>
        )}

        {/* Footer with checkbox + buttons */}
        <div className="px-6 py-4 border-t border-white/5 bg-[#08080E] space-y-3">
          <label className={`flex items-start gap-3 text-xs ${scrolledEnd ? "text-white cursor-pointer" : "text-[#606070] cursor-not-allowed"}`}>
            <input
              type="checkbox"
              data-testid="terms-accept-checkbox"
              disabled={!scrolledEnd}
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              className="mt-0.5 w-4 h-4 rounded border-white/20 accent-[#00F0FF] flex-shrink-0"
            />
            <span className="leading-relaxed">{terms.footer.acceptCheckbox}</span>
          </label>
          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <Link
              to="/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[11px] text-[#00F0FF] hover:underline inline-flex items-center gap-1"
              data-testid="terms-read-fullpage"
            >
              <ScrollText className="w-3 h-3" /> {terms.footer.readMore}
            </Link>
            <div className="sm:ml-auto flex gap-2">
              <button
                data-testid="terms-decline-btn"
                onClick={() => { window.location.href = "/"; }}
                className="px-4 py-2 rounded-lg text-xs text-[#A0A0B0] hover:text-white hover:bg-white/5 transition-colors"
              >
                {terms.footer.declineButton}
              </button>
              <button
                data-testid="terms-accept-btn"
                onClick={accept}
                disabled={!scrolledEnd || !checked || submitting}
                className="btn-primary px-5 py-2 rounded-lg text-xs font-['Outfit'] font-semibold inline-flex items-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />}
                {terms.footer.acceptButton}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
